import type * as ChartJs from 'chart.js';
import dayjs from 'dayjs';

import type { StyledDataPoint } from '@components/Charts/Line/HealthLineChart';

import type { ParameterBoundary, ParameterModel } from 'generated/types';

export function groupByIntervalAndAverage(
  data: StyledDataPoint[],
  minDate: Date,
  maxDate: Date
): StyledDataPoint[] {
  const groupedData: Record<
    string,
    { sum: number; count: number; pointStyle: ChartJs.PointStyle }
  > = {};
  const interval = determineXTimeInput(minDate, maxDate);

  data.forEach((point) => {
    const date = new Date(point.x || '');
    let intervalKey: number;
    if (interval === 'quarter') {
      intervalKey = dayjs(date).startOf('year').toDate().getTime();
    } else if (interval && interval !== 'hour') {
      intervalKey = dayjs(date).startOf(interval).toDate().getTime();
    } else {
      intervalKey = date.getTime();
    }

    if (!(intervalKey in groupedData)) {
      groupedData[intervalKey] = {
        sum: 0,
        count: 0,
        pointStyle: point.pointStyle,
      };
    }

    groupedData[intervalKey].sum += point.y || 0;
    groupedData[intervalKey].count += 1;
    if (point.pointStyle === false) {
      groupedData[intervalKey].pointStyle = point.pointStyle;
    }
  });

  // Calculate average for each interval
  return Object.entries(groupedData).map(([key, value]) => {
    return {
      x: parseInt(key),
      y: value.sum / value.count,
      pointStyle: value.pointStyle,
    };
  });
}

export const getBoundaryValues = (
  parameter: ParameterModel
): [number, number] => {
  const { lower_bound, upper_bound } = parameter;

  function getLower(
    lower?: ParameterBoundary | null,
    upper?: ParameterBoundary | null
  ) {
    if (!lower?.value) {
      return 0;
      // If lower and upper bounds are 8, make lower bound 7.9 on graph
    } else if (lower.value === upper?.value) {
      return lower.value - 0.1;
      // If lower bound is not inclusive (only gt), make bound 7.1 instead of 7
    } else if (!lower.inclusive) {
      return lower.value + 0.1;
    }
    return lower.value;
  }

  function getUpper(
    lower?: ParameterBoundary | null,
    upper?: ParameterBoundary | null
  ) {
    if (!upper) {
      return 0;
      // If lower and upper bounds are 8, make upper bound 8.1 on graph
    } else if (lower?.value === upper.value) {
      return upper.value + 0.1;
      // If upper bound is not inclusive (only lt), make bound 6.9 instead of 7
    } else if (!upper.inclusive) {
      return upper.value - 0.1;
    }
    return upper.value;
  }

  return [
    getLower(lower_bound, upper_bound),
    getUpper(lower_bound, upper_bound),
  ];
};

export const determineXTimeInput = (
  minDate: Date,
  maxDate: Date
): ChartJs.TimeUnit | undefined => {
  if (dayjs(maxDate).diff(minDate, 'day') < 7) {
    return 'hour';
  } else if (dayjs(maxDate).diff(minDate, 'week') < 4) {
    return 'day';
  } else if (dayjs(maxDate).diff(minDate, 'month') < 12) {
    return 'week';
  } else if (dayjs(maxDate).diff(minDate, 'year') <= 5) {
    return 'month';
  } else if (dayjs(maxDate).diff(minDate, 'year') <= 15) {
    return 'year';
  }
  return 'quarter';
};
